//src/utils/logger.js
import CryptoJS from "crypto-js";
import { getActiveAccountId } from "./account-context";

const LOGGING_ENABLED = (process.env.REACT_APP_PUSH_LOGS || "true").toLowerCase() === "true";

const ENCRYPTION_KEY = process.env.REACT_APP_LOG_ENCRYPTION_KEY || "my-secret-key";
const OFFLINE_LOG_KEY = "offline_logs";

// Log level priority
const LOG_LEVELS = {
  debug: 1,
  info: 2,
  warn: 3,
  error: 4,
  silent: 0
};
const CURRENT_LOG_LEVEL = LOG_LEVELS[process.env.REACT_APP_LOG_LEVEL || "error"];

console.log("Logger Init", {
  LOGGING_ENABLED,
  CURRENT_LOG_LEVEL
});


const shouldLog = (level) => {
  const levelValue = LOG_LEVELS[level] ?? LOG_LEVELS.info;
  return CURRENT_LOG_LEVEL !== LOG_LEVELS.silent && levelValue >= CURRENT_LOG_LEVEL;
};
// Encrypt log
const encrypt = (data) =>
  CryptoJS.AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY).toString();

// Decrypt log
const decrypt = (cipher) =>
  JSON.parse(CryptoJS.AES.decrypt(cipher, ENCRYPTION_KEY).toString(CryptoJS.enc.Utf8));

// Save encrypted log to localStorage with max size protection
const MAX_OFFLINE_LOGS = 10;

// Save encrypted log to localStorage
const saveOfflineLog = (log) => {
  try {
    // Flush all logs if level is silent
    if (CURRENT_LOG_LEVEL === "silent") {
      localStorage.removeItem(OFFLINE_LOG_KEY);
      return;
    }

    // Skip if the log's level doesn't pass the threshold
    if (!shouldLog(log.level))
      return;

    const stored = localStorage.getItem(OFFLINE_LOG_KEY);
    const logs = stored ? JSON.parse(stored) : [];

    // Encrypt and push new log
    logs.push(encrypt(log));

    // Keep only latest MAX_OFFLINE_LOGS
    const trimmedLogs = logs.slice(-MAX_OFFLINE_LOGS);

    localStorage.setItem(OFFLINE_LOG_KEY, JSON.stringify(trimmedLogs));
  } catch (e) {
    if (CURRENT_LOG_LEVEL !== "silent") {
      console.error("Error saving offline log", e);
    }
  }
};


// Push offline logs when back online
const flushOfflineLogs = async () => {
  if (!LOGGING_ENABLED)
  {
    return;
  }


  const encryptedLogs = localStorage.getItem(OFFLINE_LOG_KEY);
  if (!encryptedLogs) return;

  try {
    const logs = JSON.parse(encryptedLogs).map(decrypt);
    for (const log of logs) {
      await logToServer(log);
    }
    localStorage.removeItem(OFFLINE_LOG_KEY);
  } catch (e) {
    console.error("Failed to flush offline logs", e);
  }
};


window.addEventListener("online", flushOfflineLogs);

const enrich = (level, message, context = []) => ({
  level,
  message,
  context,
  timestamp: new Date().toISOString(),
  userId: getUser()?.id || null,
  path: window.location.pathname,
  userAgent: navigator.userAgent,
  appVersion: process.env.REACT_APP_VERSION,
});

const getUser = () => {
  try {
    const user = JSON.parse(localStorage.getItem("userdata"));
    return user || null;
  } catch {
    return null;
  }
};

export const logToServer = async (log) => {

  if (!LOGGING_ENABLED) {
    return;
  }
   // Skip if the log's level doesn't pass the threshold
  if (!shouldLog(log.level))
  {
    return;
  }

  try {
    console.log(log);
    const encrypted = encrypt(log); // 🔐 Encrypt the whole log
    const accountId = getActiveAccountId(window.location.pathname);
    const response = await fetch(`${process.env.REACT_APP_API_URL}/ui-logs`, {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify({encrypted, account_id: accountId}) // ✅ send as { encrypted }
    });

    if (!response.ok) {
      console.warn('Failed to send log: Server responded with status', response.status);
    }
  } catch (err) {
    console.warn("❌ Failed to send log:", err.message);
    saveOfflineLog(log);
  }
};


const logger = {
  debug: (msg, ...ctx) => {
    if (shouldLog("debug")) {
      console.debug("[DEBUG]", msg, ...ctx);
      logToServer(enrich("debug", msg, ctx));
    }
  },
  info: (msg, ...ctx) => {
    if (shouldLog("info")) logToServer(enrich("info", msg, ctx));
  },
  warn: (msg, ...ctx) => {
    if (shouldLog("warn")) logToServer(enrich("warn", msg, ctx));
  },
  error: (msg, ...ctx) => {
    if (shouldLog("error")) logToServer(enrich("error", msg, ctx));
  },
  flushOfflineLogs,
};

export default logger;

